
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { CheckCircle, AlertTriangle, Lightbulb, Brain, ArrowRight, Info } from "lucide-react";
import { FallacyAnalysis } from "@/types/fallacyAnalysis";
import { FallacyDetailModal } from "./FallacyDetailModal";
import { getConfidenceColor, getConfidenceText } from "@/utils/ui/confidenceHelpers";
import { MarkdownRenderer, PlainTextRenderer } from "@/components/ui/MarkdownRenderer";
import { analyzeContent } from "@/utils/text/contentRenderer";
import { UI_TEXT } from "@/constants/ui";
import { useFallacyModal } from "@/hooks/ui/useFallacyModal";
import type { FallacyData } from "@/types/flipped";

interface AnalysisResultsProps {
  analysis: FallacyAnalysis;
}

export const AnalysisResults = ({ analysis }: AnalysisResultsProps) => {
  const { selectedFallacy, selectedFallacyData, isModalOpen, openModal, closeModal } = useFallacyModal();

  const handleFallacyClick = (fallacyType: string) => {
    const fallacyData: FallacyData | null = analysis.fallacies?.find(f => f.type === fallacyType) || 
      (analysis.fallacyType === fallacyType ? {
        type: analysis.fallacyType,
        confidence: analysis.confidence,
        explanation: analysis.explanation,
        reasoning: analysis.reasoning
      } : null);
    
    openModal(fallacyType, fallacyData);
  };

  const detectedFallacies = analysis.fallacies || (analysis.fallacyType ? [{ 
    type: analysis.fallacyType, 
    confidence: analysis.confidence, 
    explanation: analysis.explanation,
    reasoning: analysis.reasoning 
  }] : []);

  const renderContent = (content: string, className?: string) => {
    const { normalized, shouldUseMarkdown } = analyzeContent(content);
    return shouldUseMarkdown
      ? <MarkdownRenderer content={normalized} className={className} />
      : <PlainTextRenderer content={normalized} className={className} />;
  };

  const issuesText = detectedFallacies.length > 1 
    ? UI_TEXT.messages.logicalIssuesFound 
    : UI_TEXT.messages.logicalFallacyDetected;

  const issuesLabel = detectedFallacies.length > 1 
    ? UI_TEXT.labels.hasIssues 
    : UI_TEXT.labels.hasIssue;

  return (
    <div className="space-y-4 sm:space-y-6 animate-in slide-in-from-bottom-4 duration-500">
      {/* Main Result */}
      <Card className="border-0 shadow-lg bg-white/80 backdrop-blur-sm">
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2 text-lg sm:text-xl">
              {analysis.hasFallacy ? (
                <>
                  <AlertTriangle className="w-5 h-5 text-orange-600 flex-shrink-0" />
                  <span className="leading-tight">{issuesText}</span>
                </>
              ) : (
                <>
                  <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                  <span className="leading-tight">{UI_TEXT.messages.noSignificantIssues}</span>
                </>
              )}
            </CardTitle>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {analysis.hasFallacy && detectedFallacies.length > 0 && (
            <div className="space-y-4">
              <div className="space-y-3">
                <h4 className="font-medium text-slate-700 text-sm sm:text-base">
                  {UI_TEXT.headers.issuesDetected} ({detectedFallacies.length}):
                </h4>
                <div className="flex flex-wrap gap-3">
                  {detectedFallacies.map((fallacy, index) => (
                    <Button
                      key={index}
                      variant="outline"
                      onClick={() => handleFallacyClick(fallacy.type)}
                      className="h-auto p-3 text-left hover:bg-orange-50 border-orange-200 cursor-pointer"
                    >
                      <div className="space-y-2">
                        <div className="font-semibold text-orange-800 text-sm">
                          {fallacy.type}
                        </div>
                        <div className="flex items-center gap-2 text-xs">
                          <div className="flex-1 h-1.5 bg-slate-200 rounded-full overflow-hidden min-w-[60px]">
                            <div 
                              className={`h-full ${getConfidenceColor(fallacy.confidence)} transition-all duration-500`}
                              style={{ width: `${fallacy.confidence}%` }}
                            />
                          </div>
                          <span className="text-slate-600 font-medium flex-shrink-0">
                            {fallacy.confidence}%
                          </span>
                        </div>
                      </div>
                    </Button>
                  ))}
                </div>
              </div>

              <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
                <span className="text-sm font-medium text-slate-600 flex-shrink-0">
                  {UI_TEXT.headers.overallConfidence}:
                </span>
                <div className="flex items-center gap-2 w-full sm:w-auto">
                  <div className="flex-1 sm:w-24 h-2 bg-slate-200 rounded-full overflow-hidden">
                    <div 
                      className={`h-full ${getConfidenceColor(analysis.confidence)} transition-all duration-500`}
                      style={{ width: `${analysis.confidence}%` }}
                    />
                  </div>
                  <span className="text-sm font-medium text-slate-700 flex-shrink-0">
                    {analysis.confidence}% ({getConfidenceText(analysis.confidence)})
                  </span>
                </div>
              </div>
            </div>
          )}
          
          <Alert className={analysis.hasFallacy ? "border-orange-200 bg-orange-50" : "border-green-200 bg-green-50"}>
            <AlertDescription className="text-sm sm:text-base leading-relaxed">
              {renderContent(analysis.explanation, "text-slate-700")}
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>

      {/* AI Reasoning */}
      <Card className="border-0 shadow-lg bg-white/80 backdrop-blur-sm">
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center gap-2 text-lg sm:text-xl">
            <Brain className="w-5 h-5 text-indigo-600 flex-shrink-0" />
            {UI_TEXT.headers.analysisReasoning}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {renderContent(analysis.reasoning, "text-sm sm:text-base text-slate-700")}
          
            {analysis.alternativeConsiderations && (
              <Alert className="border-blue-200 bg-blue-50">
                <Info className="w-4 h-4 flex-shrink-0" />
                <AlertDescription className="text-sm sm:text-base leading-relaxed">
                  <strong className="block mb-1">{UI_TEXT.labels.alternativeConsiderations}:</strong>
                  {renderContent(analysis.alternativeConsiderations, "text-slate-700")}
                </AlertDescription>
              </Alert>
            )}
        </CardContent>
      </Card>

      {/* Better Version */}
      {analysis.hasFallacy && analysis.improvedArgument && (
        <Card className="border-0 shadow-lg bg-gradient-to-br from-blue-50 to-indigo-50">
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center gap-2 text-lg sm:text-xl">
              <Lightbulb className="w-5 h-5 text-blue-600 flex-shrink-0" />
              {UI_TEXT.headers.betterWayToSayIt}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 sm:space-y-6">
            <div className="grid gap-4 lg:grid-cols-2">
              <div className="space-y-2">
                <h4 className="font-semibold text-slate-800 flex items-center gap-2 text-sm sm:text-base">
                  {UI_TEXT.labels.originalArgument}
                  <Badge variant="destructive" className="text-xs">
                    {issuesLabel}
                  </Badge>
                </h4>
                <div className="p-3 sm:p-4 bg-red-50 border border-red-200 rounded-lg">
                  {renderContent(analysis.originalArgument, "text-xs sm:text-sm text-slate-700")}
                </div>
              </div>
              
              <div className="space-y-2">
                <h4 className="font-semibold text-slate-800 flex items-center gap-2 text-sm sm:text-base">
                  <ArrowRight className="w-4 h-4" />
                  {UI_TEXT.labels.improvedVersion}
                  <Badge variant="default" className="text-xs bg-green-600">
                    {UI_TEXT.labels.strongerLogic}
                  </Badge>
                </h4>
                <div className="p-3 sm:p-4 bg-green-50 border border-green-200 rounded-lg">
                  {renderContent(analysis.improvedArgument, "text-xs sm:text-sm text-slate-700")}
                </div>
              </div>
            </div>
            
            {analysis.counterStrategy && (
              <Alert className="border-blue-200 bg-blue-50">
                <Lightbulb className="w-4 h-4 flex-shrink-0" />
                <AlertDescription className="text-sm sm:text-base leading-relaxed">
                  <strong className="block mb-1">{UI_TEXT.labels.howToRespond}:</strong>
                  {renderContent(analysis.counterStrategy, "text-slate-700")}
                </AlertDescription>
              </Alert>
            )}
          </CardContent>
        </Card>
      )}

      {/* Enhanced Modal with AI Analysis */}
      <FallacyDetailModal
        fallacyType={selectedFallacy}
        fallacyData={selectedFallacyData}
        isOpen={isModalOpen}
        onClose={closeModal}
      />
    </div>
  );
};
